import { useState, useEffect, useRef } from 'react';
import { api, usePortfolio } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import PortfolioSelector from './PortfolioSelector';
import UnitSearch from './UnitSearch';
import MobileMenu from './MobileMenu';
import AppFolioImport from './AppFolioImport';
import ExportPortfolio from './ExportPortfolio';
import AnalyticsPage from './AnalyticsPage';

export default function OwnerDashboard({ user, onLogout }) {
  const { activePortfolioId, portfolios, refreshPortfolios } = usePortfolio();
  const portfolioRef = useRef(null);
  const searchRef = useRef(null);
  
  const [units, setUnits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [sheetConfig, setSheetConfig] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showHidden, setShowHidden] = useState(true);
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'analytics'
  const [includeLipInOccupancy, setIncludeLipInOccupancy] = useState(() => {
    try {
      const v = window.localStorage.getItem('ur_includeLipInOccupancy');
      return v === null ? true : v === '1';
    } catch (_) {
      return true;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('ur_includeLipInOccupancy', includeLipInOccupancy ? '1' : '0');
    } catch (_) { /* ignore */ }
  }, [includeLipInOccupancy]);
  
  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [questionableRows, setQuestionableRows] = useState([]);
  const [syncId, setSyncId] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [invitePassword, setInvitePassword] = useState('');
  
  // Active portfolio from context
  const activePortfolio = portfolios.find(p => p.id === activePortfolioId);
  
  // AppFolio Integration
  const [appfolioConfig, setAppfolioConfig] = useState(null);
  const [appfolioSubdomain, setAppfolioSubdomain] = useState('');
  const [syncingListings, setSyncingListings] = useState(false);
  const [pendingMatches, setPendingMatches] = useState([]);
  const [showMatchReviewModal, setShowMatchReviewModal] = useState(false);
  const [showManualLinkModal, setShowManualLinkModal] = useState(false);
  const [selectedUnitForLink, setSelectedUnitForLink] = useState(null);
  const [manualListingUrl, setManualListingUrl] = useState('');

  // AppFolio scheduled-report import
  const [pendingImportCount, setPendingImportCount] = useState(0);
  
  // Vacant Unit Status Edit
  const [showVacantStatusModal, setShowVacantStatusModal] = useState(false);
  const [selectedUnitForVacantStatus, setSelectedUnitForVacantStatus] = useState(null);
  const [vacantStatusValue, setVacantStatusValue] = useState('');
  
  // Unit Status Update Modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedUnitForStatus, setSelectedUnitForStatus] = useState(null);
  const [newStatusValue, setNewStatusValue] = useState('');
  
  // Add existing agent
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  
  // Valid status options
  const STATUS_OPTIONS = [
    'Vacant-unturned',
    'Vacant-Ready',
    'Lease-in-Progress',
    'Offline',
    'Current'
  ];

  // Load data when active portfolio changes
  useEffect(() => {
    if (activePortfolioId) {
      loadData();
      loadAppfolioConfig();
      loadPendingImportCount();
    }
  }, [activePortfolioId]);

  const loadPendingImportCount = async () => {
    if (!activePortfolioId) return;
    try {
      const res = await api.get(`/imports/pending?portfolio_id=${activePortfolioId}`);
      setPendingImportCount((res.data?.imports || []).length);
    } catch (e) {
      // non-fatal — badge stays at 0
      setPendingImportCount(0);
    }
  };

  const loadData = async () => {
    if (!activePortfolioId) return;
    
    setLoading(true);
    try {
      const [unitsRes, requestsRes, usersRes, configRes] = await Promise.all([
        api.get(`/units?portfolio_id=${activePortfolioId}`),
        api.get(`/status-requests?portfolio_id=${activePortfolioId}`),
        api.get(`/portfolios/${activePortfolioId}/users`),
        api.get(`/sheets/config?portfolio_id=${activePortfolioId}`)
      ]);
      
      setUnits(unitsRes.data);
      setRequests(requestsRes.data);
      setUsers(usersRes.data);
      setSheetConfig(configRes.data);
      if (configRes.data) {
        setSheetUrl(configRes.data.sheet_url);
      } else {
        setSheetUrl('');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAppfolioConfig = async () => {
    if (!activePortfolioId) return;
    
    try {
      const response = await api.get(`/appfolio/config?portfolio_id=${activePortfolioId}`);
      setAppfolioConfig(response.data);
      if (response.data?.subdomain) {
        setAppfolioSubdomain(response.data.subdomain);
      } else {
        setAppfolioSubdomain('');
      }
      // Also load pending matches
      const matchesRes = await api.get(`/appfolio/pending-matches?portfolio_id=${activePortfolioId}`);
      setPendingMatches(matchesRes.data || []);
    } catch (error) {
      setAppfolioConfig(null);
      setAppfolioSubdomain('');
      setPendingMatches([]);
    }
  };

  const handleConfigureAppfolio = async (e) => {
    e.preventDefault();
    if (!activePortfolioId) {
      toast.error('No portfolio selected');
      return;
    }
    try {
      await api.post('/appfolio/configure', { subdomain: appfolioSubdomain, portfolio_id: activePortfolioId });
      toast.success('AppFolio configured successfully');
      loadAppfolioConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure AppFolio');
    }
  };

  const handleSyncListings = async () => {
    if (!activePortfolioId) return;
    setSyncingListings(true);
    try {
      const response = await api.post(`/appfolio/sync-listings?portfolio_id=${activePortfolioId}`);
      toast.success(`Sync complete: ${response.data.auto_matched} auto-matched, ${response.data.pending_review} need review`);
      loadData();
      loadAppfolioConfig();
      if (response.data.pending_review > 0) {
        setShowMatchReviewModal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync listings');
    } finally {
      setSyncingListings(false);
    }
  };

  const handleReviewMatch = async (matchId, action, customUrl = null) => {
    try {
      await api.post(`/appfolio/pending-matches/${matchId}/review`, { 
        action,
        listing_url: customUrl 
      });
      toast.success(action === 'confirm' ? 'Match confirmed' : 'Match rejected');
      loadData();
      loadAppfolioConfig();
    } catch (error) {
      toast.error('Failed to review match');
    }
  };

  const handleManualLink = async () => {
    if (!selectedUnitForLink || !manualListingUrl) return;
    
    try {
      await api.post('/appfolio/manual-link', {
        unit_id: selectedUnitForLink.id,
        listing_url: manualListingUrl
      });
      toast.success('Listing linked successfully');
      setShowManualLinkModal(false);
      setSelectedUnitForLink(null);
      setManualListingUrl('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link listing');
    }
  };

  const handleRemoveListingLink = async (unitId) => {
    try {
      await api.delete(`/appfolio/units/${unitId}/listing`);
      toast.success('Listing link removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove listing link');
    }
  };

  const openManualLinkModal = (unit) => {
    setSelectedUnitForLink(unit);
    setManualListingUrl(unit.listing_url || '');
    setShowManualLinkModal(true);
  };

  const openVacantStatusModal = (unit) => {
    setSelectedUnitForVacantStatus(unit);
    setVacantStatusValue(unit.vacant_unit_status || '');
    setShowVacantStatusModal(true);
  };

  // Load available agents from other portfolios
  const loadAvailableAgents = async () => {
    if (!activePortfolioId) return;
    
    setLoadingAgents(true);
    try {
      const response = await api.get(`/portfolios/${activePortfolioId}/available-agents`);
      setAvailableAgents(response.data);
    } catch (error) {
      console.error('Failed to load available agents');
      setAvailableAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const openAddAgentModal = () => {
    loadAvailableAgents();
    setShowAddAgentModal(true);
  };

  const handleAddExistingAgent = async (agentId) => {
    if (!activePortfolioId) return;
    
    try {
      await api.post(`/portfolios/${activePortfolioId}/grant-access`, { user_id: agentId });
      toast.success('Agent added to portfolio!');
      loadData();
      loadAvailableAgents(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add agent');
    }
  };

  const handleUpdateVacantStatus = async () => {
    if (!selectedUnitForVacantStatus) return;
    
    try {
      await api.post(`/units/${selectedUnitForVacantStatus.id}/update-vacant-status`, {
        vacant_unit_status: vacantStatusValue
      });
      toast.success('Vacant unit status updated successfully');
      setShowVacantStatusModal(false);
      setSelectedUnitForVacantStatus(null);
      setVacantStatusValue('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vacant unit status');
    }
  };

  const handleConfigureSheet = async (e) => {
    e.preventDefault();
    if (!activePortfolioId) {
      toast.error('No portfolio selected');
      return;
    }
    try {
      await api.post('/sheets/configure', { sheet_url: sheetUrl, portfolio_id: activePortfolioId });
      toast.success('Sheet configured successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure sheet');
    }
  };

  const handleSyncSheet = async () => {
    if (!activePortfolioId) return;
    setSyncing(true);
    try {
      const response = await api.post(`/sheets/sync?portfolio_id=${activePortfolioId}`);
      
      if (response.data.needs_review) {
        // Show review modal
        setQuestionableRows(response.data.questionable_rows);
        setSyncId(response.data.sync_id);
        // Pre-select all rows
        const allRowNumbers = new Set(response.data.questionable_rows.map(r => r.row_number));
        setSelectedRows(allRowNumbers);
        setShowReviewModal(true);
        toast.info(`Found ${response.data.questionable_rows.length} rows that need review`);
      } else {
        toast.success(response.data.message);
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync sheet');
    } finally {
      setSyncing(false);
    }
  };

  const handleConfirmSync = async () => {
    if (!activePortfolioId) return;
    try {
      const response = await api.post(`/sheets/confirm-sync?sync_id=${syncId}&portfolio_id=${activePortfolioId}`, {
        approved_rows: Array.from(selectedRows)
      });
      toast.success(response.data.message);
      setShowReviewModal(false);
      setQuestionableRows([]);
      setSyncId(null);
      setSelectedRows(new Set());
      loadData();
    } catch (error) {
      toast.error('Failed to complete sync');
    }
  };

  const toggleRowSelection = (rowNumber) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowNumber)) {
      newSelected.delete(rowNumber);
    } else {
      newSelected.add(rowNumber);
    }
    setSelectedRows(newSelected);
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    if (!activePortfolioId) {
      toast.error('No portfolio selected');
      return;
    }
    
    // Validate password
    if (invitePassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.post('/users/create-direct', { 
        email: inviteEmail, 
        role: inviteRole,
        password: invitePassword,
        portfolio_id: activePortfolioId
      });
      toast.success('User created successfully! They can now log in with the provided credentials.');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('agent');
      setInvitePassword('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleTerminateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to terminate this user?')) return;
    
    try {
      await api.post(`/users/${userId}/terminate`);
      toast.success('User terminated successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate user');
    }
  };

  const handleReviewRequest = async (requestId, action) => {
    try {
      await api.post(`/status-requests/${requestId}/review`, { action });
      toast.success(`Request ${action === 'approve' ? 'approved' : 'declined'} successfully`);
      setShowRequestModal(false);
      setSelectedRequest(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };

  const openRequestModal = (request) => {
    setSelectedRequest(request);
    setShowRequestModal(true);
  };

  const pendingRequests = requests.filter(r => r.request_status === 'pending');
  
  const handleToggleVisibility = async (unitId) => {
    try {
      await api.post(`/units/${unitId}/toggle-visibility`);
      toast.success('Unit visibility updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleChangeStatus = (unit) => {
    setSelectedUnitForStatus(unit);
    setNewStatusValue(unit.status);
    setShowStatusModal(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!selectedUnitForStatus || !newStatusValue) return;
    
    if (newStatusValue === selectedUnitForStatus.status) {
      setShowStatusModal(false);
      return;
    }
    
    try {
      await api.post(`/units/${selectedUnitForStatus.id}/update-status`, { status: newStatusValue });
      toast.success('Unit status updated');
      setShowStatusModal(false);
      setSelectedUnitForStatus(null);
      setNewStatusValue('');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const visibleUnits = showHidden ? units : units.filter(u => !u.hidden);
  const totalCount = units.filter(u => !u.hidden).length;

  // Calculate status counts (excluding hidden units) - normalize to lowercase for consistency
  const statusCounts = units.filter(u => !u.hidden).reduce((acc, unit) => {
    const normalizedStatus = unit.status.toLowerCase();
    acc[normalizedStatus] = (acc[normalizedStatus] || 0) + 1;
    return acc;
  }, {});

  // Group units by address/building
  const unitsByAddress = visibleUnits.reduce((acc, unit) => {
    const address = unit.address || 'No Address';
    if (!acc[address]) {
      acc[address] = [];
    }
    acc[address].push(unit);
    return acc;
  }, {});

  if (loading && !activePortfolioId) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="owner-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1 data-testid="dashboard-title" className="brand-title">
            <img src="/favicon.png" alt="" className="brand-mark" />
            <span className="brand-text">Unit Relay</span>
            <span className="role-badge">Owner</span>
          </h1>
          <PortfolioSelector ref={portfolioRef} userRole="owner" />
          <UnitSearch ref={searchRef} userRole="owner" />
        </div>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => setView(view === 'analytics' ? 'dashboard' : 'analytics')}
            data-testid="nav-analytics-button"
            title="Portfolio analytics"
          >
            {view === 'analytics' ? 'Back to Dashboard' : 'Analytics'}
          </button>
          <span className="user-email" data-testid="user-email">{user.email}</span>
          <button className="btn-secondary" onClick={onLogout} data-testid="logout-button">
            Logout
          </button>
        </div>
        <MobileMenu
          portfolioRef={portfolioRef}
          searchRef={searchRef}
          userRole="owner"
          userEmail={user.email}
          onLogout={onLogout}
        />
      </div>

      {!activePortfolioId ? (
        <div className="dashboard-content">
          <div className="empty-state" style={{ padding: '60px', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '16px' }}>No Portfolio Selected</h2>
            <p style={{ color: '#64748b' }}>Create a new portfolio or join an existing one using the controls above.</p>
          </div>
        </div>
      ) : loading ? (
        <div className="loading-screen" style={{ minHeight: '400px' }}>
          <div className="loading-spinner"></div>
        </div>
      ) : view === 'analytics' ? (
        <AnalyticsPage
          portfolioId={activePortfolioId}
          portfolioName={activePortfolio?.name}
          onBack={() => setView('dashboard')}
        />
      ) : (
      <div className="dashboard-content">
        <Tabs defaultValue="dashboard" className="dashboard-tabs">
          <TabsList className="tabs-nav" data-testid="owner-tabs">
            <TabsTrigger value="dashboard" data-testid="dashboard-tab">Dashboard</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="integrations-tab">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Integrations
                {pendingImportCount > 0 && (
                  <span
                    data-testid="integrations-tab-badge"
                    style={{
                      background: '#f59e0b',
                      color: '#fff',
                      borderRadius: 999,
                      minWidth: 20,
                      height: 20,
                      padding: '0 6px',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {pendingImportCount}
                  </span>
                )}
              </span>
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">User Management</TabsTrigger>
            <TabsTrigger value="requests" data-testid="requests-tab">Change Requests</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
        {/* Status Summary */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Portfolio Status Summary</h2>
          </div>
          
          <div className="status-summary-grid" data-testid="status-summary">
            <div className="status-card status-vacant-unturned">
              <div className="status-card-count">{statusCounts['vacant-unturned'] || 0}</div>
              <div className="status-card-label">Vacant-Unturned</div>
            </div>
            
            <div className="status-card status-vacant-ready">
              <div className="status-card-count">{statusCounts['vacant-ready'] || 0}</div>
              <div className="status-card-label">Vacant-Ready</div>
            </div>
            
            <div className="status-card status-lease-in-progress">
              <div className="status-card-count">{statusCounts['lease-in-progress'] || 0}</div>
              <div className="status-card-label">Lease-in-Progress</div>
            </div>
            
            <div className="status-card status-current">
              <div className="status-card-count">{statusCounts['current'] || 0}</div>
              <div className="status-card-label">Current</div>
            </div>
            
            <div className="status-card status-offline">
              <div className="status-card-count">{statusCounts['offline'] || 0}</div>
              <div className="status-card-label">Offline</div>
            </div>
          </div>
        </div>

        {/* Google Sheets and AppFolio sections moved to "Integrations" tab */}

        {/* Pending Status Change Requests */}
        {pendingRequests.length > 0 && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Pending Status Change Requests</h2>
              <span className="status-badge status-pending" data-testid="pending-count">
                {pendingRequests.length} Pending
              </span>
            </div>
            
            <table className="units-table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th>Agent</th>
                  <th>New Status</th>
                  <th>Tenant</th>
                  <th>Rent</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((request) => (
                  <tr key={request.id} data-testid={`request-row-${request.id}`}>
                    <td>{request.unit_info.unit}</td>
                    <td>{request.agent_email}</td>
                    <td>
                      <span className={`status-badge status-${request.new_status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {request.new_status}
                      </span>
                    </td>
                    <td>{request.tenant_name}</td>
                    <td>${request.rent_amount}</td>
                    <td>{new Date(request.requested_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn-action"
                        onClick={() => openRequestModal(request)}
                        data-testid={`review-request-${request.id}`}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Units Table */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Portfolio Units</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <span className="user-email" data-testid="units-count">
                {totalCount} units (showing {visibleUnits.length})
              </span>
              <ExportPortfolio
                portfolioName={activePortfolio?.name}
                unitsByAddress={unitsByAddress}
                defaultIncludeLip={includeLipInOccupancy}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                  data-testid="show-hidden-toggle"
                />
                Show hidden units
              </label>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}
                title="When checked, Lease-in-Progress units count as occupied. When unchecked, they remain in the rentable pool but are not counted as occupied."
              >
                <input
                  type="checkbox"
                  checked={includeLipInOccupancy}
                  onChange={(e) => setIncludeLipInOccupancy(e.target.checked)}
                  data-testid="include-lip-toggle"
                />
                Count Lease-in-Progress as occupied
              </label>
            </div>
          </div>
          
          {visibleUnits.length > 0 ? (
            <Accordion type="multiple" className="buildings-accordion">
              {Object.entries(unitsByAddress).map(([address, addressUnits]) => {
                // Occupancy denominator: all non-hidden units (including
                // Offline units, per product spec). Hidden units never count.
                // Numerator: units with status `Current`. Lease-in-Progress
                // is included only when the user toggles the
                // "Count Lease-in-Progress as occupied" checkbox; otherwise
                // it stays in the denominator (rentable, not occupied).
                const rentable = addressUnits.filter((u) => !u.hidden);
                const occupied = rentable.filter((u) => {
                  const s = (u.status || '').toLowerCase();
                  if (s === 'current') return true;
                  if (s === 'lease-in-progress' && includeLipInOccupancy) return true;
                  return false;
                }).length;
                const denom = rentable.length;
                const pct = denom > 0 ? Math.round((occupied / denom) * 100) : 0;
                const bandColor =
                  denom === 0
                    ? '#94a3b8'
                    : pct >= 90
                    ? '#10b981'
                    : pct >= 75
                    ? '#3b82f6'
                    : pct >= 50
                    ? '#f59e0b'
                    : '#ef4444';
                return (
                <AccordionItem key={address} value={address} data-testid={`building-${address}`}>
                  <AccordionTrigger className="building-trigger">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%', paddingRight: '16px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '600', fontSize: '16px', flex: '1 1 240px', minWidth: 0 }}>
                        {address}
                      </span>
                      <div
                        data-testid={`building-occupancy-${address}`}
                        title={`${occupied} of ${denom} ${denom === 1 ? 'unit' : 'units'} occupied${includeLipInOccupancy ? ' (Lease-in-Progress counted as occupied)' : ' (Lease-in-Progress not counted as occupied)'}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}
                      >
                        <div
                          style={{
                            width: 80,
                            height: 8,
                            background: '#e2e8f0',
                            borderRadius: 999,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              background: bandColor,
                              transition: 'width 220ms ease',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: bandColor,
                            minWidth: 38,
                            textAlign: 'right',
                          }}
                        >
                          {denom === 0 ? '—' : `${pct}%`}
                        </span>
                      </div>
                      <span style={{ color: '#64748b', fontSize: '14px', flex: '0 0 auto' }}>
                        {addressUnits.length} {addressUnits.length === 1 ? 'unit' : 'units'}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <table className="units-table">
                      <thead>
                        <tr>
                          <th>Unit</th>
                          <th>BD/BA</th>
                          <th>Status</th>
                          <th>Sqft</th>
                          <th>Vacant Unit Status</th>
                          <th>Listing</th>
                          <th>Last Updated</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addressUnits.map((unit) => (
                          <tr 
                            key={unit.id} 
                            data-testid={`unit-row-${unit.id}`}
                            style={{ opacity: unit.hidden ? 0.5 : 1 }}
                          >
                            <td>
                              {unit.unit}
                              {unit.hidden && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#94a3b8' }}>(Hidden)</span>}
                            </td>
                            <td>{unit.bd_ba}</td>
                            <td>
                              <button
                                onClick={() => handleChangeStatus(unit)}
                                style={{ 
                                  background: 'none', 
                                  border: 'none', 
                                  cursor: 'pointer',
                                  padding: 0
                                }}
                                title="Click to change status"
                                data-testid={`edit-status-${unit.id}`}
                              >
                                <span className={`status-badge status-${unit.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                  {unit.status}
                                  <span style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.7 }}>✏️</span>
                                </span>
                              </button>
                            </td>
                            <td>{unit.sqft}</td>
                            <td>
                              <button
                                onClick={() => openVacantStatusModal(unit)}
                                style={{ 
                                  background: 'none', 
                                  border: 'none', 
                                  cursor: 'pointer',
                                  color: unit.vacant_unit_status ? '#1e293b' : '#94a3b8',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '14px'
                                }}
                                title="Click to edit vacant unit status"
                                data-testid={`edit-vacant-status-${unit.id}`}
                              >
                                {unit.vacant_unit_status || '-'}
                                <span style={{ fontSize: '10px', color: '#94a3b8' }}>✏️</span>
                              </button>
                            </td>
                            <td>
                              {unit.listing_url ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <a
                                    href={unit.listing_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-action"
                                    style={{ 
                                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                      textDecoration: 'none',
                                      fontSize: '12px',
                                      padding: '4px 8px'
                                    }}
                                    data-testid={`view-listing-${unit.id}`}
                                  >
                                    View Listing
                                  </a>
                                  <button
                                    onClick={() => handleRemoveListingLink(unit.id)}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: '#ef4444', 
                                      cursor: 'pointer',
                                      fontSize: '16px',
                                      padding: '0'
                                    }}
                                    title="Remove link"
                                  >
                                    ×
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => openManualLinkModal(unit)}
                                  style={{ 
                                    background: 'none', 
                                    border: '1px dashed #94a3b8', 
                                    color: '#64748b', 
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    borderRadius: '6px'
                                  }}
                                  data-testid={`link-listing-${unit.id}`}
                                >
                                  + Link
                                </button>
                              )}
                            </td>
                            <td>{new Date(unit.last_updated).toLocaleDateString()}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  className="btn-action"
                                  onClick={() => handleChangeStatus(unit)}
                                  data-testid={`change-status-${unit.id}`}
                                  title="Change status"
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn-action"
                                  onClick={() => handleToggleVisibility(unit.id)}
                                  data-testid={`toggle-visibility-${unit.id}`}
                                  title={unit.hidden ? 'Show unit' : 'Hide unit'}
                                  style={{ background: unit.hidden ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #64748b 0%, #475569 100%)' }}
                                >
                                  {unit.hidden ? 'Show' : 'Hide'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
              );
              })}
            </Accordion>
          ) : (
            <div className="empty-state" data-testid="empty-units">
              <p>No units found. Configure and sync your Google Sheet to get started.</p>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="integrations">
            {/* AppFolio Scheduled-Report Import (Resend Inbound) */}
            <AppFolioImport
              portfolioId={activePortfolioId}
              onPendingCount={setPendingImportCount}
            />

            {/* Google Sheet Configuration */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>Google Sheets Configuration</h2>
              </div>
              
              {sheetConfig && sheetConfig.last_synced && (
                <div className="sheet-info" data-testid="sheet-info">
                  <p><strong>Last synced:</strong> {new Date(sheetConfig.last_synced).toLocaleString()}</p>
                  <small>Sheet URL: {sheetConfig.sheet_url}</small>
                </div>
              )}

              <form className="sheet-config-form" onSubmit={handleConfigureSheet}>
                <div className="form-group">
                  <label htmlFor="sheetUrl">Google Sheet URL (Public/CSV Export)</label>
                  <input
                    id="sheetUrl"
                    type="url"
                    value={sheetUrl}
                    onChange={(e) => setSheetUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/.../edit"
                    required
                    data-testid="sheet-url-input"
                  />
                </div>
                <button type="submit" className="btn-primary" data-testid="configure-sheet-button">
                  Configure
                </button>
                {sheetConfig && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSyncSheet}
                    disabled={syncing}
                    data-testid="sync-sheet-button"
                  >
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </button>
                )}
              </form>
            </div>

            {/* AppFolio Integration */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>AppFolio Listing Integration</h2>
                {appfolioConfig?.enabled && (
                  <span className="status-badge" style={{ background: '#10b981' }}>Active</span>
                )}
              </div>
              
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>
                Connect your AppFolio account to automatically link Vacant-Ready units to their listings.
              </p>
              
              {appfolioConfig?.last_synced && (
                <div className="sheet-info" style={{ marginBottom: '16px' }}>
                  <p><strong>Last synced:</strong> {new Date(appfolioConfig.last_synced).toLocaleString()}</p>
                  <small>Subdomain: {appfolioConfig.subdomain}.appfolio.com</small>
                </div>
              )}

              <form className="sheet-config-form" onSubmit={handleConfigureAppfolio}>
                <div className="form-group">
                  <label htmlFor="appfolioSubdomain">AppFolio Subdomain</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ color: '#64748b' }}>https://</span>
                    <input
                      id="appfolioSubdomain"
                      type="text"
                      value={appfolioSubdomain}
                      onChange={(e) => setAppfolioSubdomain(e.target.value.toLowerCase())}
                      placeholder="yourcompany"
                      style={{ flex: 1 }}
                      data-testid="appfolio-subdomain-input"
                    />
                    <span style={{ color: '#64748b' }}>.appfolio.com</span>
                  </div>
                </div>
                <button type="submit" className="btn-primary" data-testid="configure-appfolio-button">
                  Configure
                </button>
                {appfolioConfig?.enabled && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSyncListings}
                    disabled={syncingListings}
                    data-testid="sync-listings-button"
                  >
                    {syncingListings ? 'Syncing Listings...' : 'Sync Listings'}
                  </button>
                )}
              </form>
              
              {/* Pending Matches Alert */}
              {pendingMatches.length > 0 && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '16px', 
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                  borderRadius: '12px',
                  border: '2px solid #f59e0b'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ color: '#92400e' }}>⚠️ {pendingMatches.length} listings need your review</strong>
                      <p style={{ color: '#92400e', fontSize: '14px', marginTop: '4px' }}>
                        These matches have less than 80% confidence and need manual confirmation.
                      </p>
                    </div>
                    <button
                      className="btn-primary"
                      onClick={() => setShowMatchReviewModal(true)}
                      style={{ background: '#f59e0b' }}
                    >
                      Review Matches
                    </button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="users">
            {/* Portfolio Token */}
            {activePortfolio && (
              <div className="dashboard-section">
                <div className="section-header">
                  <h2>Portfolio Token: {activePortfolio.name}</h2>
                </div>
                <div style={{ padding: '24px', background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', borderRadius: '16px', border: '2px solid #6ee7b7' }}>
                  <p style={{ marginBottom: '12px', color: '#065f46', fontSize: '14px' }}>
                    Share this token with team members so they can join your portfolio:
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ 
                      fontSize: '32px', 
                      fontWeight: '700', 
                      fontFamily: 'monospace', 
                      color: '#059669',
                      letterSpacing: '4px',
                      background: 'white',
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '2px solid #6ee7b7'
                    }} data-testid="portfolio-token">
                      {activePortfolio.token}
                    </div>
                    <button
                      className="btn-sm"
                      onClick={() => {
                        navigator.clipboard.writeText(activePortfolio.token);
                        toast.success('Token copied to clipboard!');
                      }}
                      data-testid="copy-token-button"
                    >
                      Copy Token
                    </button>
                  </div>
                  <p style={{ marginTop: '12px', color: '#059669', fontSize: '13px' }}>
                    Users can register as agents using this token to join your portfolio
                  </p>
                </div>
              </div>
            )}
            
            {/* User Management */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>User Management</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {portfolios.length > 1 && (
                    <button
                      className="btn-sm"
                      onClick={openAddAgentModal}
                      style={{ background: '#10b981' }}
                      data-testid="add-existing-agent-button"
                    >
                      Add Existing Agent
                    </button>
                  )}
                  <button
                    className="btn-sm"
                    onClick={() => setShowInviteModal(true)}
                    data-testid="invite-user-button"
                  >
                    Create User
                  </button>
                </div>
              </div>
              
              <div className="users-list">
                {users.map((u) => (
                  <div key={u.id} className="user-card" data-testid={`user-card-${u.id}`}>
                    <div className="user-info">
                      <strong>{u.email}</strong>
                      <span>
                        {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                        <span className={`user-status ${u.is_active ? 'active' : 'inactive'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                    </div>
                    {u.id !== user.id && u.is_active && (
                      <button
                        className="btn-action btn-danger"
                        onClick={() => handleTerminateUser(u.id)}
                        data-testid={`terminate-user-${u.id}`}
                      >
                        Terminate
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="requests">
            {/* All Status Change Requests */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>All Status Change Requests</h2>
              </div>
              
              {requests.length > 0 ? (
                <table className="units-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Agent</th>
                      <th>New Status</th>
                      <th>Tenant</th>
                      <th>Status</th>
                      <th>Requested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} data-testid={`all-request-row-${request.id}`}>
                        <td>{request.unit_info.unit}</td>
                        <td>{request.agent_email}</td>
                        <td>{request.new_status}</td>
                        <td>{request.tenant_name}</td>
                        <td>
                          <span className={`status-badge status-${request.request_status}`}>
                            {request.request_status}
                          </span>
                        </td>
                        <td>{new Date(request.requested_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <p>No status change requests yet.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      )}

      {/* Create User Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInviteUser}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px 0' }}>
              <div className="form-group">
                <Label htmlFor="inviteEmail">Email Address</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="invitePassword">Password</Label>
                <Input
                  id="invitePassword"
                  type="password"
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  placeholder="Create password for user"
                  required
                  minLength={6}
                  data-testid="invite-password-input"
                />
                <small style={{ color: '#64748b', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  User can log in immediately with this password
                </small>
              </div>
              <div className="form-group">
                <Label htmlFor="inviteRole">Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="send-invite-button">Create User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Request Review Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent data-testid="review-modal">
          <DialogHeader>
            <DialogTitle>Review Status Change Request</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="request-details">
              <div className="detail-row">
                <strong>Agent:</strong>
                <span>{selectedRequest.agent_email}</span>
              </div>
              <div className="detail-row">
                <strong>Unit:</strong>
                <span>{selectedRequest.unit_info.unit}</span>
              </div>
              <div className="detail-row">
                <strong>Address:</strong>
                <span>{selectedRequest.unit_info.address || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <strong>New Status:</strong>
                <span>{selectedRequest.new_status}</span>
              </div>
              <div className="detail-row">
                <strong>Tenant Name:</strong>
                <span>{selectedRequest.tenant_name}</span>
              </div>
              <div className="detail-row">
                <strong>Rent Amount:</strong>
                <span>${selectedRequest.rent_amount}</span>
              </div>
              <div className="detail-row">
                <strong>Move-in Date:</strong>
                <span>{selectedRequest.move_in_date}</span>
              </div>
              {selectedRequest.subsidy_program && (
                <div className="detail-row">
                  <strong>Subsidy Program:</strong>
                  <span>{selectedRequest.subsidy_program}</span>
                </div>
              )}
              <div className="detail-row">
                <strong>Requested:</strong>
                <span>{new Date(selectedRequest.requested_at).toLocaleString()}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleReviewRequest(selectedRequest.id, 'decline')}
              data-testid="decline-request-button"
            >
              Decline
            </Button>
            <Button
              onClick={() => handleReviewRequest(selectedRequest.id, 'approve')}
              data-testid="approve-request-button"
            >
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Questionable Rows Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent style={{ maxWidth: '800px' }} data-testid="review-rows-modal">
          <DialogHeader>
            <DialogTitle>Review Units Before Sync</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '20px 0' }}>
            <p style={{ marginBottom: '20px', color: '#64748b', fontSize: '14px' }}>
              The following rows have issues and need your review. All other valid rows will be included automatically.
            </p>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
              <table className="units-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>Include</th>
                    <th>Unit</th>
                    <th>BD/BA</th>
                    <th>Status</th>
                    <th>Sqft</th>
                    <th>Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {questionableRows.map((row) => (
                    <tr key={row.row_number} data-testid={`questionable-row-${row.row_number}`}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(row.row_number)}
                          onChange={() => toggleRowSelection(row.row_number)}
                          data-testid={`checkbox-row-${row.row_number}`}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                      </td>
                      <td>
                        {row.unit}
                        {row.address && <div style={{ fontSize: '12px', color: '#64748b' }}>{row.address}</div>}
                      </td>
                      <td>{row.bd_ba || '-'}</td>
                      <td>{row.status || '-'}</td>
                      <td>{row.sqft || '-'}</td>
                      <td>
                        <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '500' }}>
                          {row.reason}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '14px', color: '#475569' }}>
              {selectedRows.size} of {questionableRows.length} rows selected
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setShowReviewModal(false);
                setQuestionableRows([]);
                setSyncId(null);
                setSelectedRows(new Set());
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSync}
              data-testid="confirm-sync-button"
            >
              Sync {selectedRows.size} Units
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AppFolio Match Review Modal */}
      <Dialog open={showMatchReviewModal} onOpenChange={setShowMatchReviewModal}>
        <DialogContent style={{ maxWidth: '900px' }} data-testid="match-review-modal">
          <DialogHeader>
            <DialogTitle>Review Listing Matches</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '20px 0' }}>
            <p style={{ marginBottom: '20px', color: '#64748b', fontSize: '14px' }}>
              The following matches have less than 80% confidence. Please review and confirm or reject each match.
            </p>
            
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {pendingMatches.map((match) => (
                <div 
                  key={match.id} 
                  style={{ 
                    padding: '16px', 
                    marginBottom: '12px', 
                    background: '#f8fafc', 
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}
                  data-testid={`pending-match-${match.id}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <strong style={{ fontSize: '16px' }}>Unit: {match.unit_name}</strong>
                      <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>{match.unit_address}</p>
                    </div>
                    <span style={{ 
                      background: match.confidence >= 60 ? '#fef3c7' : '#fee2e2',
                      color: match.confidence >= 60 ? '#92400e' : '#991b1b',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      {match.confidence}% confidence
                    </span>
                  </div>
                  
                  <div style={{ 
                    background: 'white', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    marginBottom: '12px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <p style={{ fontSize: '14px', marginBottom: '4px' }}>
                      <strong>Suggested Listing:</strong>
                    </p>
                    <p style={{ color: '#64748b', fontSize: '14px' }}>{match.listing.address}</p>
                    <a 
                      href={match.listing.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#3b82f6', fontSize: '14px' }}
                    >
                      {match.listing.url}
                    </a>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      size="sm"
                      onClick={() => handleReviewMatch(match.id, 'confirm')}
                      data-testid={`confirm-match-${match.id}`}
                    >
                      Confirm Match
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReviewMatch(match.id, 'reject')}
                      data-testid={`reject-match-${match.id}`}
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setSelectedUnitForLink({ id: match.unit_id, unit: match.unit_name });
                        setManualListingUrl('');
                        setShowMatchReviewModal(false);
                        setShowManualLinkModal(true);
                      }}
                    >
                      Enter Different URL
                    </Button>
                  </div>
                </div>
              ))}
              
              {pendingMatches.length === 0 && (
                <p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
                  No pending matches to review.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMatchReviewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Link Modal */}
      <Dialog open={showManualLinkModal} onOpenChange={setShowManualLinkModal}>
        <DialogContent data-testid="manual-link-modal">
          <DialogHeader>
            <DialogTitle>Link Listing to Unit</DialogTitle>
          </DialogHeader>
          {selectedUnitForLink && (
            <div style={{ padding: '20px 0' }}>
              <p style={{ marginBottom: '16px' }}>
                Linking listing to unit: <strong>{selectedUnitForLink.unit}</strong>
              </p>
              <div className="form-group">
                <Label htmlFor="listingUrl">Listing URL</Label>
                <Input
                  id="listingUrl"
                  type="url"
                  value={manualListingUrl}
                  onChange={(e) => setManualListingUrl(e.target.value)}
                  placeholder="https://yourcompany.appfolio.com/listings/detail/..."
                  data-testid="manual-listing-url-input"
                />
                <small style={{ color: '#64748b', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  Enter the full URL to the listing page
                </small>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowManualLinkModal(false);
              setSelectedUnitForLink(null);
              setManualListingUrl('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleManualLink}
              disabled={!manualListingUrl}
              data-testid="save-manual-link-button"
            >
              Save Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vacant Unit Status Edit Modal */}
      <Dialog open={showVacantStatusModal} onOpenChange={setShowVacantStatusModal}>
        <DialogContent data-testid="vacant-status-modal">
          <DialogHeader>
            <DialogTitle>Edit Vacant Unit Status</DialogTitle>
          </DialogHeader>
          {selectedUnitForVacantStatus && (
            <div style={{ padding: '20px 0' }}>
              <p style={{ marginBottom: '16px' }}>
                Editing status for unit: <strong>{selectedUnitForVacantStatus.unit}</strong>
                {selectedUnitForVacantStatus.address && (
                  <span style={{ color: '#64748b', display: 'block', fontSize: '14px' }}>
                    {selectedUnitForVacantStatus.address}
                  </span>
                )}
              </p>
              <div className="form-group">
                <Label htmlFor="vacantStatus">Vacant Unit Status</Label>
                <Input
                  id="vacantStatus"
                  type="text"
                  value={vacantStatusValue}
                  onChange={(e) => setVacantStatusValue(e.target.value)}
                  placeholder="e.g., Ready for showing, Needs cleaning, etc."
                  data-testid="vacant-status-input"
                />
                <small style={{ color: '#64748b', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                  This status is visible only to owners, not agents
                </small>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowVacantStatusModal(false);
              setSelectedUnitForVacantStatus(null);
              setVacantStatusValue('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateVacantStatus}
              data-testid="save-vacant-status-button"
            >
              Save Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Existing Agent Modal */}
      <Dialog open={showAddAgentModal} onOpenChange={setShowAddAgentModal}>
        <DialogContent style={{ maxWidth: '600px' }} data-testid="add-agent-modal">
          <DialogHeader>
            <DialogTitle>Add Existing Agent to Portfolio</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '20px 0' }}>
            <p style={{ marginBottom: '16px', color: '#64748b', fontSize: '14px' }}>
              Select an agent from your other portfolios to add to <strong>{activePortfolio?.name}</strong>
            </p>
            
            {loadingAgents ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              </div>
            ) : availableAgents.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {availableAgents.map((agent) => (
                  <div 
                    key={agent.id} 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      marginBottom: '8px',
                      border: '1px solid #e2e8f0'
                    }}
                    data-testid={`available-agent-${agent.id}`}
                  >
                    <div>
                      <strong style={{ display: 'block' }}>{agent.email}</strong>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>
                        Currently in: {agent.portfolios?.map(p => p.name).join(', ') || 'No portfolios'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddExistingAgent(agent.id)}
                      data-testid={`add-agent-btn-${agent.id}`}
                    >
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                <p>No available agents found.</p>
                <p style={{ fontSize: '13px', marginTop: '8px' }}>
                  All your agents are already in this portfolio, or you don't have agents in other portfolios yet.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAgentModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unit Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent data-testid="status-modal">
          <DialogHeader>
            <DialogTitle>Update Unit Status</DialogTitle>
          </DialogHeader>
          {selectedUnitForStatus && (
            <div style={{ padding: '20px 0' }}>
              <p style={{ marginBottom: '16px' }}>
                Updating status for unit: <strong>{selectedUnitForStatus.unit}</strong>
                {selectedUnitForStatus.address && (
                  <span style={{ color: '#64748b', display: 'block', fontSize: '14px' }}>
                    {selectedUnitForStatus.address}
                  </span>
                )}
              </p>
              <div className="form-group">
                <Label htmlFor="newStatus">Status</Label>
                <Select value={newStatusValue} onValueChange={setNewStatusValue}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowStatusModal(false);
              setSelectedUnitForStatus(null);
              setNewStatusValue('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmStatusUpdate}
              data-testid="save-status-button"
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
